import { useCallback, useEffect, useRef, useState } from 'react';
import type { ContextEntryRead, RagHitRef } from '@localagent/sdk';
import type { ContextRagMetadata } from './progress-tree';

export type GetRagHits = (refs: RagHitRef[]) => Promise<ContextEntryRead[]>;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function laneLabel(taskId: string): string {
  return taskId === 'session' ? 'chat history' : 'agent task history';
}

function HitRow({ hit, index }: { hit: ContextEntryRead; index: number }) {
  if (!hit.found) {
    return (
      <li data-testid={`context-rag-hit-${index}`} data-found="false" className="font-sans text-muted-foreground">
        This entry is no longer in the session history.
      </li>
    );
  }
  return (
    <li data-testid={`context-rag-hit-${index}`} data-found="true" className="space-y-0.5">
      <div className="flex items-center gap-2 font-sans text-muted-foreground">
        <span className="rounded bg-chart-6/15 px-1 capitalize text-chart-6">
          {hit.role}
        </span>
        <span>{laneLabel(hit.task_id)}</span>
        {hit.excerpt && (
          <span data-testid="context-rag-hit-excerpt">
            excerpt — {formatBytes(new TextEncoder().encode(hit.content).length)} of {formatBytes(hit.total_bytes)}
          </span>
        )}
      </div>
      <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap">{hit.content}</pre>
    </li>
  );
}

export function ContextRagDetail({ label, meta, getRagHits }: {
  label: string;
  meta: ContextRagMetadata;
  getRagHits: GetRagHits;
}) {
  const refs = meta.hit_refs ?? [];
  const refsKey = refs.map((r) => `${r.task_id}/${r.message_index}`).join(',');
  const shouldLoad = meta.outcome === 'hits' && refs.length > 0;
  const [hits, setHits] = useState<ContextEntryRead[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refsRef = useRef(refs);

  useEffect(() => {
    refsRef.current = refs;
  });

  const load = useCallback(async () => {
    setError(null);
    setHits(null);
    try {
      setHits(await getRagHits(refsRef.current));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [getRagHits]);

  useEffect(() => {
    if (shouldLoad) void load();
  }, [shouldLoad, load, refsKey]);

  const state = !shouldLoad ? 'none' : error !== null ? 'failed' : hits === null ? 'loading' : 'loaded';

  return (
    <div
      data-testid="progress-step-context-rag-detail"
      data-state={state}
      className="rounded bg-muted/30 p-2 text-micro font-mono space-y-1.5"
    >
      <div className="font-sans font-medium text-foreground text-caption">{label}</div>
      {meta.indexed_entries != null && (
        <div className="font-sans text-muted-foreground">searched {meta.indexed_entries} history entries</div>
      )}
      {meta.outcome === 'empty' && (
        <div className="font-sans text-muted-foreground">Nothing relevant was found, so the model got no retrieved context.</div>
      )}
      {meta.outcome === 'failed' && (
        <div className="font-sans text-error-text">Retrieval failed; the run continued without retrieved context.</div>
      )}
      {state === 'loading' && <div className="font-sans text-muted-foreground">Loading what the model was given…</div>}
      {state === 'failed' && (
        <div className="font-sans text-error-text">
          Could not load the retrieved entries: {error}{' '}
          <button
            type="button"
            data-testid="context-rag-detail-retry"
            className="underline"
            onClick={(e) => {
              e.stopPropagation();
              void load();
            }}
          >
            Retry
          </button>
        </div>
      )}
      {state === 'loaded' && hits && (
        <>
          <div className="font-sans text-muted-foreground">
            Given to the model before its first step, as background:
          </div>
          <ol className="space-y-2">
            {hits.map((hit, i) => (
              <HitRow key={`${hit.task_id}/${hit.message_index}`} hit={hit} index={i} />
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
